import { useEffect, useState } from 'react';
import {
  Cradle,
  PaymentMethod,
  StripePriceProduct,
  SubscriptionType as SubscriptionTypeEnum,
  UserSubscription,
} from '@solo/app-core';
import { toast } from 'react-toastify';
import { Button, Flex, Icon, Img, Link, Section, Text } from '@/app/ui';
import { translate } from '@/app/i18n';
import Colors from '@/app/styles/Colors';
import { useCradle } from '@/app/contexts';
import { dayjs } from '@/app/utils';
import visalogo from '@/app/assets/images/visa-logo.png';
import AddPaymentMethodModal from '@/app/ui/organisms/Profile/SubscriptionComponent/AddPaymentMethodModal';
import ManageSubscriptionModal from '@/app/ui/organisms/Profile/SubscriptionComponent/ManageSubscriptionModal';
import { SubscriptionInfo } from '@/app/ui/organisms/SubscriptionCard/SubscriptionCard';
import Typography from '@/app/ui/atoms/Typography';

interface CardNumberProps {
  last4: number;
}

const CardNumber = (props: CardNumberProps) => {
  const { last4 } = props;
  return (
    <Text.Paragraph
      style={{ marginLeft: 16, marginRight: 16, width: '100%', minWidth: 'fit-content' }}
    >{`**** **** **** ${last4}`}</Text.Paragraph>
  );
};

interface SubscriptionTypeProps {
  subscriptionType: SubscriptionTypeEnum | undefined;
}
const SubscriptionType = (props: SubscriptionTypeProps) => {
  const { subscriptionType } = props;

  if (!subscriptionType) {
    return null;
  }

  return (
    <>
      <Text.BigItalic style={{ fontStyle: 'italic' }}>
        {subscriptionType === SubscriptionTypeEnum.ALL
          ? translate('SUBSCRIPTION_INFO.ALL_RIGHTS')
          : translate('SUBSCRIPTION_INFO.DISTRIBUTION')}
      </Text.BigItalic>
      <Text.Span
        style={{
          fontSize: '14px',
          color: Colors.grey,
          paddingLeft: '4px',
          marginRight: '16px',
        }}
      >
        {subscriptionType === SubscriptionTypeEnum.ALL
          ? translate('SUBSCRIPTION_INFO.ALL_RIGHTS_DETAIL')
          : translate('SUBSCRIPTION_INFO.DISTRIBUTION_RIGHTS_DETAIL')}
      </Text.Span>
    </>
  );
};

const SubscriptionComponent = () => {
  const [cards, setCards] = useState<PaymentMethod[]>([]);
  const [manageSubscriptionModalOpen, setManageSubscriptionModalOpen] = useState(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  // TODO
  const subsTypeLink = 'https:solo.com/help/subscrition-types';
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription>();
  const [product, setProduct] = useState<StripePriceProduct['product']>();
  const { paymentBloc } = useCradle<Cradle>();

  async function getCardInfo() {
    try {
      const myCards = await paymentBloc.getStripePaymentMethods();
      setCards(myCards);
    } catch (error) {
      toast.error(translate('TOAST.CARD_ERROR'));
    }
  }

  async function getProduct(productId: string) {
    if (!productId) {
      return undefined;
    }
    const prices = await paymentBloc.getStripePriceProducts();
    const products = prices.flatMap((price) => price.product);
    const p = products.find((pr) => pr.id === productId);
    return p;
  }

  async function getSubscription() {
    let subscription: UserSubscription;
    try {
      subscription = await paymentBloc.getSubscriptionInfo();
      setCurrentSubscription(subscription);
      if (subscription?.productId) {
        const p = await getProduct(subscription?.productId);
        setProduct(p);
      }
    } catch (error) {
      await paymentBloc.createStripeAccount();
      subscription = await paymentBloc.getSubscriptionInfo();
    }
    setCurrentSubscription(subscription);
    if (subscription?.productId) {
      const p = await getProduct(subscription?.productId);
      setProduct(p);
    }
  }

  useEffect(() => {
    getSubscription();
    getCardInfo();
  }, []);

  const deleteCard = async (cardId: string) => {
    try {
      await paymentBloc.deleteStripePaymentMethod(cardId);
      setCards(cards.filter((card) => card.id !== cardId));
    } catch (error) {
      toast.error(translate('TOAST.CARD_ERROR'));
    }
  };

  const onSelectSubscription = async (subscription: SubscriptionInfo) => {
    if (!subscription.priceId) {
      toast.error(translate('TOAST.SUBSCRIPTION_ERROR'));
      return;
    }
    try {
      await paymentBloc.cancelSubscription();
      await paymentBloc.createSubscription(subscription.priceId);
    } catch (error) {
      toast.error(translate('TOAST.SUBSCRIPTION_ERROR'));
    }
    getSubscription();
  };

  const setDefaultPaymentMethod = async (cardId: string) => {
    try {
      await paymentBloc.setDefaultPaymentMethod(cardId);
      getSubscription();
      getCardInfo();
    } catch (error) {
      toast.error(translate('TOAST.CARD_ERROR'));
    }
  };

  const onCancelSubscription = async () => {
    try {
      await paymentBloc.cancelSubscription();
      setManageSubscriptionModalOpen(false);
    } catch (error) {
      toast.error(translate('TOAST.SUBSCRIPTION_ERROR'));
    }
  };

  return (
    <>
      <Section py={15} style={{ flexDirection: 'column', paddingTop: 0 }}>
        <Text.SectionTitle style={{ marginBottom: '24px' }}>
          {translate('PROFILE_PAGES.SUBSCRIPTION')}
        </Text.SectionTitle>
        <Flex.Container alignItems="baseline">
          {/* <Typography.Paragraph style={{ marginRight: '10px' }}>
            {translate('SUBSCRIPTION_INFO.STATUS')}: {currentSubscription?.currentSubStatus}
          </Typography.Paragraph> */}
          <Typography.H2 fontSize={20} fontWeight={700} style={{ marginRight: '10px' }}>
            {product?.name || '-'}
          </Typography.H2>
          <SubscriptionType subscriptionType={currentSubscription?.type} />
          <Flex.Item>
            <Button
              $transparent
              $size="md"
              style={{
                justifyContent: 'space-between',
                color: Colors.primary,
                fontWeight: 700,
                padding: 0,
              }}
              onClick={() => setManageSubscriptionModalOpen(true)}
            >
              {translate('SUBSCRIPTION_INFO.MANAGE_SUBSCRIPTION')}
            </Button>
          </Flex.Item>
        </Flex.Container>
        <Flex.Item style={{ marginTop: 10, marginBottom: 24 }}>
          <Link $transparent $underlineHover href={subsTypeLink} target="_blank" rel="noreferrer">
            <Text.Span style={{ color: Colors.grey, textDecoration: 'underline' }}>
              {translate('SUBSCRIPTION_INFO.SUBSCRIPTION_KNOW_MORE')}
            </Text.Span>
          </Link>
        </Flex.Item>
        {currentSubscription?.currentSubPeriodEnd && (
          <Flex.Item style={{ marginTop: 10 }}>
            <Text.Span>{translate('SUBSCRIPTION_INFO.SUBSCRIPTION_RENEWED_ON')}</Text.Span>
            <Text.Span style={{ fontWeight: 700 }}>
              {dayjs(currentSubscription?.currentSubPeriodEnd).format('DD, MMM YYYY')}
            </Text.Span>
          </Flex.Item>
        )}
      </Section>
      <Section py={15} style={{ flexDirection: 'column', paddingTop: 0 }}>
        <Flex.Container alignItems="center" style={{ marginBottom: '14px' }}>
          <Text.SectionTitle style={{ marginRight: '10px' }}>
            {translate('SUBSCRIPTION_INFO.PAYMENT_METHOD')}
          </Text.SectionTitle>
          <Button
            $transparent
            $size="md"
            onClick={() => setPaymentMethodModalOpen(true)}
            style={{
              color: Colors.primary,
              fontWeight: 700,
              border: `1px solid ${Colors.primary}`,
            }}
          >
            {translate('SUBSCRIPTION_INFO.ADD_PAYMENT_METHOD')}
          </Button>
        </Flex.Container>
        <Text.Paragraph style={{ marginBottom: '24px' }}>
          {translate('SUBSCRIPTION_INFO.PAYMENT_METHOD_TEXT')}
        </Text.Paragraph>
        <Flex.Container flexDirection="column" alignItems="flex-start">
          {cards.length > 0 &&
            cards.map((card: PaymentMethod) => (
              <Flex.Container key={card.id} alignItems="center">
                <Img src={visalogo} height={24} width={38} />
                <CardNumber last4={card.card?.last4 || 0} />
                <Button $transparent $size="md" onClick={() => deleteCard(card.id)}>
                  <Icon.BinIcon style={{ height: 24 }} />
                </Button>
                {card.id !== currentSubscription?.defaultPaymentMethodId && (
                  <Button
                    $transparent
                    $size="md"
                    $full
                    onClick={() => setDefaultPaymentMethod(card.id)}
                    style={{ color: Colors.primary }}
                  >
                    {translate('SUBSCRIPTION_INFO.SET_AS_DEFAULT')}
                  </Button>
                )}
              </Flex.Container>
            ))}
        </Flex.Container>
      </Section>

      {manageSubscriptionModalOpen && (
        <ManageSubscriptionModal
          isOpen={manageSubscriptionModalOpen}
          onClose={() => setManageSubscriptionModalOpen(false)}
          onSelectSubscription={onSelectSubscription}
          onCancelSubscription={onCancelSubscription}
        />
      )}

      {paymentMethodModalOpen && (
        <AddPaymentMethodModal
          isOpen={paymentMethodModalOpen}
          onClose={() => setPaymentMethodModalOpen(false)}
          onSave={() => {
            setPaymentMethodModalOpen(false);
            getCardInfo();
          }}
        />
      )}
    </>
  );
};

export default SubscriptionComponent;
